import { defineMessages } from "react-intl";

import {
	arterialConduitPlannedMessages,
	arterialPressureMonitoringPlannedMessages,
	arterialSupplyMessages,
	backupRecipientMessages,
	caudateLobeMessages,
	cellSaverUsePlannedMessages,
	estimatedBloodLossMessages,
	hepaticArterialAnatomyTypeMessages,
	immunosuppressionMessages,
	liverSizeMessages,
	portalCirculationMessages,
	portalVeinThrombosisGradeMessages,
	splenicArteryAneurysmMessages,
	validator,
	venoVenousBypassMessages
} from "lib/build/forms/ltx/greenLight";

import { FieldData, FieldsetData } from "src/features/forms";
import { genericErrorMessages } from "src/messages/generic";
import { LeafNode } from "src/modules/shared/form-explorer";

const messages = defineMessages({
	greenLight: {
		id: "form.green_light",
		defaultMessage: "Zöld fény"
	},
	liverSize: {
		id: "form.green_light.liver_size",
		defaultMessage: "Máj mérete"
	},
	caudateLobe: {
		id: "form.green_light.caudate_lobe",
		defaultMessage: "Lobus caudatus"
	},
	portalCirculation: {
		id: "form.green_light.portal_circulation",
		defaultMessage: "Portalis keringés"
	},
	portalVeinThrombosisGrade: {
		id: "form.green_light.portal_vein_thrombosis_grade",
		defaultMessage: "Vena portae trombózis fokozata"
	},
	portalVeinThrombosisComment: {
		id: "form.green_light.portal_vein_thrombosis_comment",
		defaultMessage: "Vena portae trombózis megjegyzés"
	},
	arterialSupply: {
		id: "form.green_light.arterial_supply",
		defaultMessage: "Arteriás vérellátás"
	},
	hepaticArterialAnatomyType: {
		id: "form.green_light.hepatic_arterial_anatomy_type",
		defaultMessage: "Arteria hepatica anatómia típusa"
	},
	hepaticArterialAnatomyComment: {
		id: "form.green_light.hepatic_arterial_anatomy_comment",
		defaultMessage: "Arteria hepatica anatómia megjegyzés"
	},
	splenicArteryAneurysm: {
		id: "form.green_light.splenic_artery_aneurysm",
		defaultMessage: "Arteria lienalis aneurysma"
	},
	arterialPressureMonitoringPlanned: {
		id: "form.green_light.arterial_pressure_monitoring_planned",
		defaultMessage: "Artériás nyomásmérés (tervezetten)"
	},
	arterialConduitPlanned: {
		id: "form.green_light.arterial_conduit_planned",
		defaultMessage: "Arteria conduit (tervezetten)"
	},
	estimatedBloodLoss: {
		id: "form.green_light.estimated_blood_loss",
		defaultMessage: "Várható vérveszteség"
	},
	cellSaverUsePlanned: {
		id: "form.green_light.cell_saver_use_planned",
		defaultMessage: "CellSaver (tervezetten)"
	},
	venoVenousBypass: {
		id: "form.green_light.veno_venous_bypass",
		defaultMessage: "Veno-venosus bypass"
	},
	backupRecipient: {
		id: "form.green_light.backup_recipient",
		defaultMessage: "Tartalék recipiens behívandó?"
	},
	immunosuppression: {
		id: "form.green_light.immunosuppression",
		defaultMessage: "Immunszuppresszió"
	},
	immunosuppressionOther: {
		id: "form.green_light.immunosuppression_other",
		defaultMessage: "Egyéb immunszuppresszió"
	},
	otherTechnicalDifficulties: {
		id: "form.green_light.other_technical_difficulties",
		defaultMessage: "Egyéb technikai nehézségek (műtétek)"
	}
});

const fields: { [key: string]: FieldData } = {
	liverSize: {
		type: "field",
		path: ["liverSize"],
		label: messages.liverSize,
		errorMessages: genericErrorMessages,
		input: {
			type: "button-group",
			props: { labels: liverSizeMessages }
		}
	},
	caudateLobe: {
		type: "field",
		path: ["caudateLobe"],
		label: messages.caudateLobe,
		errorMessages: genericErrorMessages,
		input: {
			type: "button-group",
			props: { labels: caudateLobeMessages }
		}
	},
	portalCirculation: {
		type: "field",
		path: ["portalCirculation"],
		label: messages.portalCirculation,
		errorMessages: genericErrorMessages,
		input: {
			type: "button-group",
			props: { labels: portalCirculationMessages }
		}
	},
	portalVeinThrombosisGrade: {
		type: "field",
		path: ["portalVeinThrombosisGrade"],
		label: messages.portalVeinThrombosisGrade,
		errorMessages: genericErrorMessages,
		input: {
			type: "button-group",
			props: { labels: portalVeinThrombosisGradeMessages }
		}
	},
	portalVeinThrombosisComment: {
		type: "field",
		path: ["portalVeinThrombosisComment"],
		label: messages.portalVeinThrombosisComment,
		errorMessages: genericErrorMessages,
		input: { type: "textarea", props: {} }
	},
	arterialSupply: {
		type: "field",
		path: ["arterialSupply"],
		label: messages.arterialSupply,
		errorMessages: genericErrorMessages,
		input: {
			type: "button-group",
			props: { labels: arterialSupplyMessages }
		}
	},
	hepaticArterialAnatomyType: {
		type: "field",
		path: ["hepaticArterialAnatomyType"],
		label: messages.hepaticArterialAnatomyType,
		errorMessages: genericErrorMessages,
		input: {
			type: "select",
			props: { labels: hepaticArterialAnatomyTypeMessages }
		}
	},
	hepaticArterialAnatomyComment: {
		type: "field",
		path: ["hepaticArterialAnatomyComment"],
		label: messages.hepaticArterialAnatomyComment,
		errorMessages: genericErrorMessages,
		input: { type: "textarea", props: {} }
	},
	splenicArteryAneurysm: {
		type: "field",
		path: ["splenicArteryAneurysm"],
		label: messages.splenicArteryAneurysm,
		errorMessages: genericErrorMessages,
		input: {
			type: "button-group",
			props: { labels: splenicArteryAneurysmMessages }
		}
	},
	arterialPressureMonitoringPlanned: {
		type: "field",
		path: ["arterialPressureMonitoringPlanned"],
		label: messages.arterialPressureMonitoringPlanned,
		errorMessages: genericErrorMessages,
		input: {
			type: "button-group",
			props: { labels: arterialPressureMonitoringPlannedMessages }
		}
	},
	arterialConduitPlanned: {
		type: "field",
		path: ["arterialConduitPlanned"],
		label: messages.arterialConduitPlanned,
		errorMessages: genericErrorMessages,
		input: {
			type: "button-group",
			props: { labels: arterialConduitPlannedMessages }
		}
	},
	estimatedBloodLoss: {
		type: "field",
		path: ["estimatedBloodLoss"],
		label: messages.estimatedBloodLoss,
		errorMessages: genericErrorMessages,
		input: {
			type: "button-group",
			props: { labels: estimatedBloodLossMessages }
		}
	},
	cellSaverUsePlanned: {
		type: "field",
		path: ["cellSaverUsePlanned"],
		label: messages.cellSaverUsePlanned,
		errorMessages: genericErrorMessages,
		input: {
			type: "button-group",
			props: { labels: cellSaverUsePlannedMessages }
		}
	},
	venoVenousBypass: {
		type: "field",
		path: ["venoVenousBypass"],
		label: messages.venoVenousBypass,
		errorMessages: genericErrorMessages,
		input: {
			type: "button-group",
			props: { labels: venoVenousBypassMessages }
		}
	},
	backupRecipient: {
		type: "field",
		path: ["backupRecipient"],
		label: messages.backupRecipient,
		errorMessages: genericErrorMessages,
		input: {
			type: "button-group",
			props: { labels: backupRecipientMessages }
		}
	},
	immunosuppression: {
		type: "field",
		path: ["immunosuppression"],
		label: messages.immunosuppression,
		errorMessages: genericErrorMessages,
		input: {
			type: "button-group",
			props: { labels: immunosuppressionMessages }
		}
	},
	immunosuppressionOther: {
		type: "field",
		path: ["immunosuppressionOther"],
		label: messages.immunosuppressionOther,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "text" } }
	},
	otherTechnicalDifficulties: {
		type: "field",
		path: ["otherTechnicalDifficulties"],
		label: messages.otherTechnicalDifficulties,
		errorMessages: genericErrorMessages,
		input: { type: "textarea", props: {} }
	}
};

const fieldset: FieldsetData = {
	rows: [
		{ columns: [{ width: 12, field: fields.liverSize }] },
		{ columns: [{ width: 12, field: fields.caudateLobe }] },
		{ columns: [{ width: 12, field: fields.portalCirculation }] },
		{ columns: [{ width: 12, field: fields.portalVeinThrombosisGrade }] },
		{ columns: [{ width: 12, field: fields.portalVeinThrombosisComment }] },
		{ columns: [{ width: 12, field: fields.arterialSupply }] },
		{ columns: [{ width: 12, field: fields.hepaticArterialAnatomyType }] },
		{
			columns: [
				{ width: 12, field: fields.hepaticArterialAnatomyComment }
			]
		},
		{ columns: [{ width: 12, field: fields.splenicArteryAneurysm }] },
		{
			columns: [
				{ width: 12, field: fields.arterialPressureMonitoringPlanned }
			]
		},
		{ columns: [{ width: 12, field: fields.arterialConduitPlanned }] },
		{ columns: [{ width: 12, field: fields.estimatedBloodLoss }] },
		{ columns: [{ width: 12, field: fields.cellSaverUsePlanned }] },
		{ columns: [{ width: 12, field: fields.venoVenousBypass }] },
		{ columns: [{ width: 12, field: fields.backupRecipient }] },
		{ columns: [{ width: 12, field: fields.immunosuppression }] },
		{ columns: [{ width: 12, field: fields.immunosuppressionOther }] },
		{ columns: [{ width: 12, field: fields.otherTechnicalDifficulties }] }
	]
};

export const node: LeafNode = {
	id: "greenLight",
	value: {
		title: messages.greenLight,
		validator,
		fieldset,
		isImportant: true
	}
};
